import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'react-fast-compare';
import _ from 'lodash';
import { Intent, Button, MenuItem } from '@blueprintjs/core';
import { Select } from '@blueprintjs/select';

// import cls from './Select.module.css';

export default class extends PureComponent {
  static propTypes = {
    'items': PropTypes.array,
    'activeItemId': PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    'value': PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    'idField': PropTypes.string,
    'labelField': PropTypes.string,
    'allowNoSelection': PropTypes.bool,
    'resetOnActiveItemSelection': PropTypes.bool,
    'minimal': PropTypes.bool,
    'outlined': PropTypes.bool,
    'small': PropTypes.bool,
    'large': PropTypes.bool,
    'intent': PropTypes.string,
    'placeholder': PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    'icon': PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    'rightIcon': PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    'data-cy': PropTypes.string,
    'onChange': PropTypes.func,
  };

  static defaultProps = {
    activeItemId: null,
    idField: 'id',
    labelField: 'label',
    allowNoSelection: true,
    resetOnActiveItemSelection: true,
    minimal: false,
    outlined: false,
    small: false,
    large: false,
    intent: Intent.NONE,
    placeholder: '(No selection)',
    onChange: () => {},
  };

  state = {
    items: [],
    activeItem: null,
  };

  componentDidMount() {
    this.onItemsChange();
  }

  componentDidUpdate(prevProps) {
    const { items, activeItemId } = this.props;
    const { items: prevItems, activeItemId: prevActiveItemId } = prevProps;

    const didItemsChange = items && !isEqual(items, prevItems);
    const didActiveItemIdChange = activeItemId && !isEqual(activeItemId, prevActiveItemId);

    if (didItemsChange || didActiveItemIdChange) {
      this.onItemsChange();
    }
  }

  isItemSelected = id => {
    const { idField } = this.props;
    const { activeItem } = this.state;
    return activeItem && activeItem[idField] === id;
  };

  filterItem = (query, item) => {
    const { labelField } = this.props;

    const normalizedText = item[labelField].toLowerCase();
    const normalizedQuery = query.toLowerCase();

    return normalizedText.indexOf(normalizedQuery) >= 0;
  };

  onItemsChange = () => {
    const { items, activeItemId, idField, allowNoSelection } = this.props;
    let activeItem;

    if (allowNoSelection) {
      activeItem = activeItemId ? _.find(items, [idField, activeItemId]) ?? null : null;
    } else {
      activeItem = activeItemId ? _.find(items, [idField, activeItemId]) ?? items[0] : items[0];
    }

    this.setState({
      items,
      activeItem,
    });
  };

  onItemSelect = item => {
    const { idField, allowNoSelection, resetOnActiveItemSelection } = this.props;

    if (this.isItemSelected(item[idField])) {
      if (!allowNoSelection || !resetOnActiveItemSelection) {
        return;
      }

      this.setState(
        {
          activeItem: null,
        },
        () => {
          this.props.onChange(null);
        }
      );
      return;
    }

    this.setState(
      {
        activeItem: item,
      },
      () => {
        this.props.onChange(item);
      }
    );
  };

  renderItem = (item, { modifiers, handleClick }) => {
    const { idField, labelField, 'data-cy': dataTest } = this.props;

    if (!modifiers.matchesPredicate) {
      return null;
    }

    return (
      <MenuItem
        shouldDismissPopover
        active={modifiers.active}
        icon={this.isItemSelected(item[idField]) ? 'tick' : 'blank'}
        text={item[labelField]}
        data-cy={`${dataTest}-menu-item`}
        onClick={handleClick}
        key={item[idField]}
      />
    );
  };

  render() {
    const {
      'items': originalItems,
      activeItemId,
      labelField,
      minimal,
      outlined,
      small,
      large,
      intent,
      placeholder,
      icon,
      rightIcon,
      'data-cy': dataTest,
      onChange,
      ...restProps
    } = this.props;
    const { items, activeItem } = this.state;

    return (
      <Select
        {...restProps}
        items={items}
        activeItem={activeItem}
        itemPredicate={this.filterItem}
        itemRenderer={this.renderItem}
        onItemSelect={this.onItemSelect}
        itemsEqual={labelField}
        noResults={<MenuItem disabled text="No matching results." data-cy={`${dataTest}-empty-menu-item`} />}
        popoverProps={{
          minimal: true,
        }}
      >
        <Button
          minimal={minimal}
          outlined={outlined}
          small={small}
          large={large}
          intent={intent}
          text={activeItem ? activeItem[labelField] : placeholder}
          icon={activeItem?.icon ? activeItem.icon : icon}
          rightIcon={rightIcon}
          data-cy={`${dataTest}-button`}
        />
      </Select>
    );
  }
}
